import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Receipt, Phone, AlertOctagon, CheckCircle2, Banknote, Eye, MessageSquare,
    Home, Loader2, Crosshair, Navigation, Undo2, CreditCard, Flag, ChevronLeft, ChevronUp, ChevronDown, Package, Store,
    Fuel, ShoppingCart, Camera,
} from 'lucide-react';
import { SkloKruh } from '../../components/ui/Sklo';
import MapaCanvas from './MapaCanvas';
import useGpsPoloha from './hooks/useGpsPoloha';
import useRouteGeometry from './hooks/useRouteGeometry';
import useGeofence from './hooks/useGeofence';
import NezastizenModal from './NezastizenModal';
import PredaniModal from './PredaniModal';
import PlatbaModal from './PlatbaModal';
import UctenkaModal from './UctenkaModal';
import ZastavkaDetailSheet from './ZastavkaDetailSheet';
import TrasaJizdy from './TrasaJizdy';
import TankovaniSheet from '../MojeSmena/TankovaniSheet';
import useDopravniInfo from './doprava/useDopravniInfo';
import { RychlostLimit, Upozorneni, TlacitkoHlasit, NabidkaHlaseni } from './doprava/Prvky';
import { jeAplikace } from '../../lib/aplikace';
import { AKCENT, OK, CHYBA, POZOR, kc, hm, Tlacitko, Stitek, Nadpis, tint, stitekPlatby, odkazNavigace, ocekavanaPlatba, sestavTrasu, cinnostMista } from './ui';

/**
 * Na cestě (návrh „Navigace rozvozu", 2026-10-12, inspirace navigací Apple).
 * Mapa přes celou obrazovku, všechno ovládání ve skle:
 *
 * - vlevo nahoře Zpět a Navigovat (Mapy.cz) pod sebou, vedle karta cíle
 *   téže výšky — vzdálenost, adresa, osa trasy; bez šipky směru a časů,
 * - dole pilulka: zákazník, stav SMS a mapy (šedé → zelené), Volat vždy,
 *   tři čísla (u zákazníka / min / vzdálenost),
 * - akce u dveří (Nezastižen, platba / Doručeno) se ukážou na místě
 *   (okruh 150 m) nebo vytažením pilulky za úchyt,
 * - druhý pokus má akce otevřené pořád, po poslední zastávce cesta zpět.
 *
 * SMS „za chvíli jsem u vás" s odkazem na mapu posílá server sám podle
 * polohy auta z Traccaru (SledovaniRozvozu); telefon polohu hlásí jen jako
 * zálohu. Rozvozce nic neťuká.
 *
 * Benzinka / obchod na trase (2026-10-29): když je na řadě, cíl i karta
 * dole patří jí — navigace, u benzinky „Účtenka" (tankování z Flotily)
 * a „Hotovo, jedu dál".
 *
 * V mobilní aplikaci se poloha telefonu nikam neposílá (zdroj je jen Traccar,
 * rozhodnuto 2026-10-12) — slouží jen mapě a trase. Displej během jízdy
 * nezhasne, jinak by WebView polohu přestal hlásit a navigace by stála.
 */
export default function ModDorucovani({ data, user, onRefresh, onReload, optimisticUpdate, onZpet, isDark }) {
    const davka = data.davka;
    const zastavky = data.zastavky ?? [];
    const mista = data.mista ?? [];

    const [config, setConfig] = useState(null);
    const [modalOpen, setModalOpen] = useState(null);  // 'nezastizen'|'platba'|'uctenka'|null
    const [detailZastavka, setDetailZastavka] = useState(null);
    const [actionBusy, setActionBusy] = useState(null);
    const [err, setErr] = useState(null);
    const [followCamera, setFollowCamera] = useState(true);
    const [vyskaListu, setVyskaListu] = useState(320);

    const { poloha } = useGpsPoloha({ enabled: true });

    useEffect(() => {
        if (!jeAplikace()) return;
        window.__flNavigace = true;   // relace.js během jízdy neodhlásí
        window.__FL_APLIKACE?.telefon?.drzetDisplej(true);
        return () => {
            window.__flNavigace = false;
            window.__FL_APLIKACE?.telefon?.drzetDisplej(false);
        };
    }, []);

    useEffect(() => {
        fetch('/api/rozvoz/config', { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : null)
            .then(setConfig)
            .catch(() => setConfig({ mapy_key: null, style_url: null, bistro: null }));
    }, []);

    // Aktuální zastávka = první, která ještě čeká.
    const aktualniIdx = useMemo(() => {
        const i = zastavky.findIndex(z => (z.stav_doruceni ?? 'ceka') === 'ceka');
        return i === -1 ? zastavky.length : i;
    }, [zastavky]);
    const zastavka = zastavky[aktualniIdx] ?? null;
    // Benzinka / obchod, když je v trase na řadě před další objednávkou.
    const misto = useMemo(() => {
        const dalsi = sestavTrasu(zastavky, mista).find(i => (i.typ === 'm'
            ? i.m.stav === 'ceka'
            : (i.z.stav_doruceni ?? 'ceka') === 'ceka'));
        return dalsi?.typ === 'm' ? dalsi.m : null;
    }, [zastavky, mista]);
    const vseHotove = zastavka === null && !misto;
    const bistro = config?.bistro || data.bistro || null;

    // Cíl: zastávka na trase / aktuální objednávka, po poslední bistro.
    const cil = misto
        ? { lat: misto.lat, lng: misto.lng }
        : zastavka
            ? { lat: zastavka.lat, lng: zastavka.lng }
            : (bistro?.lat ? { lat: bistro.lat, lng: bistro.lng } : null);
    // durationSec a zbyvaM = zbytek trasy od polohy, odpočítávají se za jízdy
    const { geometry: routeGeometry, durationSec: routeDurationSec, zbyvaM, pesiOdKonceM } = useRouteGeometry({ from: poloha, to: cil, enabled: !!cil });
    // Dopravní info (2026-10-23): limit, stopky, radary, hlášení řidičů + hlas
    const doprava = useDopravniInfo({ poloha, routeGeometry, pesiOdKonceM, enabled: true });
    const [hlaseniOtevreno, setHlaseniOtevreno] = useState(false);
    const dopravniBody = useMemo(
        () => [...doprava.hlaseni, ...doprava.radary.map(r => ({ typ: 'radar', lat: r.lat, lng: r.lng }))],
        [doprava.hlaseni, doprava.radary],
    );
    const { inZone, distanceM, etaSec } = useGeofence(poloha, cil, routeDurationSec, 180);

    const smsOdeslana = !!zastavka?.sms_blizko;
    const zakaznikVidi = !!zastavka?.zakaznik_vidi;
    const posledniPoloha = useRef(0);

    // Lokálně přepíše stav zastávky → UI hned posune na další bez čekání na
    // refetch (serializeProCockpit volá drahou ETA matici).
    const applyOptimisticZastavka = (zastavkaId, patch) => {
        optimisticUpdate?.(prev => {
            if (!prev?.zastavky) return prev;
            return { ...prev, zastavky: prev.zastavky.map(z => (z.objednavka_id === zastavkaId ? { ...z, ...patch } : z)) };
        });
    };

    // Poloha telefonu → server (~15 s) jako záloha za tracker v autě — jen web.
    useEffect(() => {
        if (jeAplikace()) return;
        if (!poloha || Date.now() - posledniPoloha.current < 15000) return;
        posledniPoloha.current = Date.now();
        fetch('/api/rozvoz/poloha', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ lat: poloha.lat, lng: poloha.lng }),
        })
            .then(r => (r.ok ? r.json() : null))
            .then(res => {
                if (res?.objednavka_id) {
                    applyOptimisticZastavka(res.objednavka_id, { sms_blizko: res.sms_odeslana, zakaznik_vidi: res.zakaznik_vidi });
                }
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poloha]);

    const callApi = async (url, body = null) => {
        setActionBusy(url);
        setErr(null);
        try {
            const r = await fetch(url, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: body ? JSON.stringify(body) : null,
            });
            const json = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(json.error || json.message || `HTTP ${r.status}`);
            // Refetch na pozadí — jinak by akce trvala tolik, kolik ETA matice.
            onRefresh?.();
            return json;
        } catch (e) {
            setErr(e?.message || String(e));
            throw e;
        } finally {
            setActionBusy(null);
        }
    };

    const handleDoruceno = async () => {
        if (!zastavka) return;
        const zId = zastavka.objednavka_id;
        applyOptimisticZastavka(zId, {
            stav_doruceni: 'doruceno',
            cas_doruceni_skutecny: new Date().toISOString(),
            stav_platby: zastavka.stav_platby || 'online_zaplaceno',
        });
        try { await callApi(`/api/rozvoz/zastavky/${zId}/doruceno`); } catch { /* refetch zarovná */ }
    };

    const handlePlatba = async (zpusob) => {
        if (!zastavka) return false;
        const zId = zastavka.objednavka_id;
        const zaplaceno = () => applyOptimisticZastavka(zId, {
            stav_doruceni: 'doruceno',
            cas_doruceni_skutecny: new Date().toISOString(),
            stav_platby: zpusob === 'hotove' ? 'zaplaceno_hotove' : 'zaplaceno_kartou',
            typ_platby: zpusob === 'hotove' ? 'hotovost' : 'karta',
            zaplaceno: true,
        });

        // Hotovost selhat nemůže — hned další zastávka.
        if (zpusob === 'hotove') {
            zaplaceno();
            setModalOpen(null);
            try { await callApi(`/api/rozvoz/zastavky/${zId}/platba`, { zpusob }); } catch { return false; }
            return true;
        }

        // Karta: čeká se na terminál; zamítnutí nechá okno otevřené s chybou.
        let res;
        try { res = await callApi(`/api/rozvoz/zastavky/${zId}/platba`, { zpusob }); } catch { return false; }
        if (!(res?.platba?.ok ?? res?.ok)) return false;
        zaplaceno();
        setModalOpen(null);
        return true;
    };

    const handleNezastizen = async () => {
        if (!zastavka) return;
        const zId = zastavka.objednavka_id;
        if ((zastavka.pokusu ?? 0) === 0) {
            // Poprvé: na konec trasy, tahle čeká dál.
            optimisticUpdate?.(prev => {
                if (!prev?.zastavky) return prev;
                const tahle = prev.zastavky.find(z => z.objednavka_id === zId);
                return { ...prev, zastavky: [...prev.zastavky.filter(z => z.objednavka_id !== zId), { ...tahle, pokusu: 1 }] };
            });
        } else {
            applyOptimisticZastavka(zId, { stav_doruceni: 'nezastizen' });
        }
        setModalOpen(null);
        try { await callApi(`/api/rozvoz/zastavky/${zId}/nezastizen`); } catch { /* refetch */ }
    };

    // Po uzavření ukáže cockpit sám „Zpět v bistru" (stav `uzavreno`).
    const handleUzavrit = async () => {
        try { await callApi(`/api/rozvoz/davky/${davka.id}/uzavrit`); } catch { return; }
        onReload?.();
    };

    // Natankováno / nakoupeno — hned na další zastávku, server přepočítá odhady.
    const handleMistoHotovo = async () => {
        if (!misto) return;
        const id = misto.id;
        optimisticUpdate?.(prev => (prev?.mista
            ? { ...prev, mista: prev.mista.map(m => (m.id === id ? { ...m, stav: 'hotovo', hotovo_v: new Date().toISOString() } : m)) }
            : prev));
        try { await callApi(`/api/rozvoz/davky/${davka.id}/mista/${id}/hotovo`); } catch { /* refetch zarovná */ }
    };
    const [tankovani, setTankovani] = useState(null);   // směna s vozidlem pro účtenku z pumpy
    const otevritTankovani = async () => {
        try {
            const r = await fetch('/api/me/smena/dnes', { credentials: 'same-origin' });
            const d = await r.json();
            if (!d?.smena?.vozidlo) { setErr('Na směně nemáš převzaté auto — účtenku nahraj v Mojí směně.'); return; }
            setTankovani(d.smena);
        } catch (e) {
            setErr(e?.message || String(e));
        }
    };

    const zpetDoBistra = zastavky.filter(z => z.stav_doruceni === 'nezastizen');
    const navigace = odkazNavigace(misto ?? zastavka ?? (bistro?.lat ? { lat: bistro.lat, lng: bistro.lng } : null));

    // Volat · Nezastižen · Doručeno/platba jsou vidět vždycky (Cockpit 2.0,
    // 2026-10-19) — dřív až v okruhu NA_MISTE_M, a při driftu GPS nebo ve
    // velkém domě se musely vytahovat ručně. Na místě (nebo šipkou) se
    // navíc rozbalí poznámka a co předat.
    const naMiste = distanceM != null && distanceM <= NA_MISTE_M;
    // Ukazujeme vzdálenost po silnici; vzdušná jen dokud trasa nedorazí.
    const zbyva = zbyvaM ?? distanceM;
    const [rucne, setRucne] = useState(null);   // null = podle polohy, true/false = vytáhl/zasunul
    const zastavkaId = zastavka?.objednavka_id;
    useEffect(() => { setRucne(null); }, [zastavkaId]);
    const druhyPokus = (zastavka?.pokusu ?? 0) >= 1;
    const rozbaleno = druhyPokus || (rucne ?? naMiste);

    // Prvky nad spodním listem (rychlost, Nahlásit, pobočka): pevná mezera
    // nad ním i se spodní lištou telefonu — dřív na něm „visely" (2026-10-26).
    const nadListem = (navic = 0) => `calc(${vyskaListu + 20 + navic}px + env(safe-area-inset-bottom, 0px))`;
    const telPobocky = config?.bistro?.telefon ? `tel:${config.bistro.telefon}` : null;
    const vpravoPrvku = (doprava.hlaseniZapnuta ? 1 : 0) + (telPobocky ? 1 : 0);

    return (
        <div style={{ position: 'relative', flex: 1, overflow: 'hidden', background: 'var(--surface-base)' }}>
            <MapaCanvas
                bistro={bistro} zastavky={zastavky}
                mista={mista} aktualniMisto={misto?.id ?? null}
                tileUrl={config?.tile_url} tileAttribution={config?.tile_attribution}
                aktualniIdx={aktualniIdx} poloha={poloha}
                routeGeometry={routeGeometry} routeDurationSec={routeDurationSec}
                routeTypy={doprava.typyUseku}
                dopravniBody={dopravniBody}
                geofenceThresholdSec={180} geofenceActive={inZone}
                followCamera={followCamera} isDark={isDark}
                onRucniPosun={() => setFollowCamera(false)}
                // Poloha se centruje do volné mapy mezi horní kartou a pilulkou.
                okrajeKamery={{ top: 150, bottom: vyskaListu }}
            />

            {/* Nahoře: Zpět a Navigovat pod sebou, vedle karta cíle téže výšky. */}
            <div style={{ position: 'absolute', top: 'calc(10px + env(safe-area-inset-top, 0px))', left: 10, right: 10, zIndex: 5, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                        <SkloKruh Icon={ChevronLeft} ikona={24} velikost={KRUH} popis="Zpět do aplikace" onClick={onZpet} isDark={isDark} style={skloMapa} />
                        <SkloKruh Icon={Navigation} ikona={22} velikost={KRUH} popis={jeAplikace() ? "Navigovat" : "Navigovat v Mapy.cz"} href={navigace || undefined} disabled={!navigace} isDark={isDark} style={{ ...skloMapa, color: AKCENT }} />
                    </div>
                    <KartaCile
                        vzdalenost={zbyva}
                        adresa={misto ? misto.nazev : zastavka ? ulice(zastavka.adresa) : 'Zpět do bistra'}
                        zastavky={zastavky} aktualniIdx={aktualniIdx}
                        onZastavka={setDetailZastavka} isDark={isDark}
                    />
                </div>
                <Upozorneni udalost={doprava.nejblizsi} styl={skloMapa} />
                {err && (
                    <div onClick={() => setErr(null)} style={{ ...skloMapa, padding: '10px 12px', borderRadius: 14, fontSize: 13, fontWeight: 700, border: `1.5px solid ${tint(CHYBA, 0.6)}`, color: 'var(--text-primary)' }}>
                        {err}
                    </div>
                )}
            </div>

            {/* Na mou polohu — vpravo dole nad listem, po ručním posunu mapy */}
            {!followCamera && (
                <button type="button" onClick={() => setFollowCamera(true)} aria-label="Na mou polohu" style={{
                    ...skloMapa, position: 'absolute', right: 12, bottom: nadListem(vpravoPrvku * 66), zIndex: 5,
                    height: 56, padding: '0 18px 0 14px', borderRadius: 28, display: 'flex', alignItems: 'center', gap: 8,
                    color: 'var(--text-primary)', font: 'inherit', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                }}>
                    <Crosshair size={22} color={AKCENT} />Na mou polohu
                </button>
            )}

            {/* Dopravní info: limit + rychlost vlevo dole, Nahlásit vpravo dole */}
            <div style={{ position: 'absolute', left: 12, bottom: nadListem(), zIndex: 5 }}>
                <RychlostLimit limit={doprava.limit} rychlostKmh={doprava.pripraveno ? doprava.rychlostKmh : null}
                    prekroceno={doprava.prekroceno} odvozeny={doprava.limitOdvozeny} styl={skloMapa} />
            </div>
            {/* Vpravo dole: zavolat na pobočku (2026-10-26) a nad ním Nahlásit. */}
            {vpravoPrvku > 0 && (
                <div style={{ position: 'absolute', right: 12, bottom: nadListem(), zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
                    {doprava.hlaseniZapnuta && <TlacitkoHlasit onClick={() => setHlaseniOtevreno(true)} />}
                    {telPobocky && (
                        <a href={telPobocky} aria-label="Volat na pobočku" style={{
                            ...skloMapa, height: 56, padding: '0 16px 0 12px', borderRadius: 28, display: 'flex', alignItems: 'center', gap: 8,
                            color: 'var(--text-primary)', fontSize: 15, fontWeight: 800, textDecoration: 'none', WebkitTapHighlightColor: 'transparent',
                        }}>
                            <Store size={22} color={AKCENT} />Pobočka
                        </a>
                    )}
                </div>
            )}
            {hlaseniOtevreno && (
                <NabidkaHlaseni rychlostKmh={doprava.rychlostKmh} onNahlasit={doprava.nahlasit} onZavrit={() => setHlaseniOtevreno(false)} />
            )}

            <Pilulka onVyska={setVyskaListu}>
                {/* Celá trasa — odjeté, aktuální, budoucí s přetahováním (2026-10-24). */}
                <TrasaJizdy davkaId={davka?.id} zastavky={zastavky} optimisticUpdate={optimisticUpdate} onChyba={setErr} />
                {misto ? (
                    <MistoNaTrase
                        misto={misto} zastavky={zastavky} aktualniIdx={aktualniIdx}
                        etaSec={routeDurationSec} distanceM={zbyva}
                        busy={!!actionBusy}
                        onUctenka={misto.druh === 'benzinka' ? otevritTankovani : null}
                        onHotovo={handleMistoHotovo}
                    />
                ) : vseHotove ? (
                    <CestaZpet zpetDoBistra={zpetDoBistra} busy={!!actionBusy} onUzavrit={handleUzavrit} />
                ) : (
                    <Zastavka
                        zastavka={zastavka}
                        poradi={aktualniIdx + 1}
                        celkem={zastavky.length}
                        etaSec={routeDurationSec}
                        distanceM={zbyva}
                        rozbaleno={rozbaleno}
                        naMiste={naMiste}
                        onPrepnout={druhyPokus ? null : () => setRucne(!rozbaleno)}
                        druhyPokus={druhyPokus}
                        busy={actionBusy}
                        smsOdeslana={smsOdeslana}
                        zakaznikVidi={zakaznikVidi}
                        onUctenka={() => setModalOpen('uctenka')}
                        onNezastizen={() => setModalOpen('nezastizen')}
                        // Zaplaceno online: ještě otázka „Proběhlo předání v pořádku?" (2026-10-23).
                        onDoruceno={() => setModalOpen('predani')}
                        onPlatba={() => setModalOpen('platba')}
                    />
                )}
            </Pilulka>

            {modalOpen === 'predani' && (
                <PredaniModal zastavka={zastavka} onConfirm={() => { setModalOpen(null); handleDoruceno(); }} onClose={() => setModalOpen(null)} isDark={isDark} />
            )}
            {modalOpen === 'nezastizen' && (
                <NezastizenModal zastavka={zastavka} onConfirm={handleNezastizen} onClose={() => setModalOpen(null)} isDark={isDark} />
            )}
            {modalOpen === 'platba' && (
                <PlatbaModal zastavka={zastavka} onPay={handlePlatba} onClose={() => setModalOpen(null)} isDark={isDark} />
            )}
            {modalOpen === 'uctenka' && zastavka && (
                <UctenkaModal zastavka={zastavka} onClose={() => setModalOpen(null)} isDark={isDark} />
            )}
            {tankovani && (
                <TankovaniSheet
                    open onClose={() => setTankovani(null)}
                    onSaved={() => setTankovani(null)}
                    vozidlo={tankovani.vozidlo} user={user} smenaId={tankovani.id}
                    isDark={isDark} isMobile
                />
            )}
            {detailZastavka && (
                <ZastavkaDetailSheet zastavka={detailZastavka} onClose={() => setDetailZastavka(null)} isDark={isDark} />
            )}
        </div>
    );
}

/** Do jaké vzdálenosti od zastávky je rozvozce „na místě" a list se vytáhne sám. */
const NA_MISTE_M = 150;
/** Kulatá tlačítka vlevo nahoře; karta cíle má výšku dvou z nich. */
const KRUH = 52;

const ulice = (adresa) => String(adresa || '—').split(',')[0];

function fmtVzdalenost(m) {
    if (m == null) return '—';
    return m >= 1000 ? `${(m / 1000).toFixed(1).replace('.', ',')} km` : `${m} m`;
}

/**
 * Karta cíle — vzdálenost, adresa a osa trasy (bistro, co dílek to
 * zastávka, cíl). Bez šipky směru a bez časů (rozhodnuto 2026-10-12).
 * Výška = dvě kulatá tlačítka vedle ní.
 */
function KartaCile({ vzdalenost, adresa, zastavky, aktualniIdx, onZastavka, isDark }) {
    return (
        <div style={{
            ...skloMapa, flex: 1, minWidth: 0, minHeight: KRUH * 2 + 8, borderRadius: 26,
            padding: '12px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 6,
            color: 'var(--text-primary)',
        }}>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtVzdalenost(vzdalenost)}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{adresa}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <KoncovyBod Icon={Home} popis="Start v bistru" />
                {zastavky.map((z, i) => {
                    const barva = z.stav_doruceni === 'doruceno' ? OK
                        : z.stav_doruceni === 'nezastizen' ? CHYBA
                            : i === aktualniIdx ? AKCENT : 'rgba(255,255,255,0.18)';
                    return (
                        <button key={z.objednavka_id} type="button" onClick={() => onZastavka?.(z)}
                            aria-label={`Zastávka ${i + 1}`}
                            style={{ flex: 1, minWidth: 0, height: 20, padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', WebkitTapHighlightColor: 'transparent' }}>
                            <span style={{ flex: 1, height: i === aktualniIdx ? 7 : 5, borderRadius: 4, background: barva }} />
                        </button>
                    );
                })}
                <KoncovyBod Icon={Flag} popis="Cíl — zpět v bistru" barva={aktualniIdx >= zastavky.length ? OK : null} />
            </div>
        </div>
    );
}

/**
 * Pilulka dole — kouřové sklo nad mapou. Výšku hlásí nahoru, ať kamera
 * centruje polohu nad ní.
 */
function Pilulka({ children, onVyska }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => onVyska?.(el.offsetHeight + 12));
        ro.observe(el);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <section ref={ref} style={{
            ...skloMapa, position: 'absolute', left: 10, right: 10,
            bottom: 'calc(10px + env(safe-area-inset-bottom, 0px))', zIndex: 6,
            borderRadius: 26, padding: 12,
            display: 'flex', flexDirection: 'column', gap: 9, color: 'var(--text-primary)',
        }}>
            {children}
        </section>
    );
}

/** Start a cíl na ose — bílé ikony v kroužku, ať je vidět, kde trasa začíná a končí. */
function KoncovyBod({ Icon, popis, barva }) {
    return (
        <span role="img" aria-label={popis} title={popis} style={{
            width: 26, height: 26, borderRadius: 99, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: barva ? tint(OK, 0.18) : 'rgba(255,255,255,0.10)',
            color: barva ?? 'var(--text-primary)',
        }}>
            <Icon size={15} strokeWidth={2.4} />
        </span>
    );
}

function Zastavka({ zastavka: z, poradi, celkem, etaSec, distanceM, rozbaleno, naMiste, onPrepnout, druhyPokus, busy, smsOdeslana, zakaznikVidi, onUctenka, onNezastizen, onDoruceno, onPlatba }) {
    const zaplaceno = ['online_zaplaceno', 'zaplaceno_hotove', 'zaplaceno_kartou'].includes(z.stav_platby);
    // Po úpravě zaplacené (online) objednávky (2026-10-27): doplatek se
    // vybírá místo celé částky, přeplatek řidič vrací hotově.
    const vratit = z.vratit_kc > 0 ? z.vratit_kc : null;
    const doplatek = z.doplatek_kc > 0 ? z.doplatek_kc : null;
    const tel = z.telefon ? `tel:${z.telefon}` : null;
    const pracuje = busy?.includes('/doruceno') || busy?.includes('/platba');
    const pozde = (z.rozdil_sec ?? 0) > 0;
    const akce = (
        // Nižší tlačítka s ikonou vedle textu (2026-10-26) — list zabíral půl mapy.
        <>
            {(doplatek || vratit) && (
                <div style={{ padding: '8px 12px', borderRadius: 12, fontSize: 15, fontWeight: 800, background: tint(vratit ? CHYBA : POZOR, 0.14), border: `1px solid ${tint(vratit ? CHYBA : POZOR, 0.5)}`, color: vratit ? CHYBA : POZOR }}>
                    {vratit ? `Vrať hostovi ${kc(vratit)} hotově — objednávka po úpravě zlevnila` : `Doplatek ${kc(doplatek)} — objednávka se po zaplacení online upravila`}
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                <Tlacitko Icon={Phone} href={tel} disabled={!tel} vyska={50} style={{ ...sklenene, padding: '0 8px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>Volat</Tlacitko>
                <Tlacitko barva={CHYBA} Icon={AlertOctagon} onClick={onNezastizen} vyska={50} style={{ ...sklenene, padding: '0 8px', fontSize: 14 }}>Nezastižen</Tlacitko>
                {zaplaceno
                    ? <Tlacitko plne barva={vratit ? CHYBA : OK} Icon={pracuje ? Loader2 : CheckCircle2} onClick={onDoruceno} disabled={pracuje} vyska={50} style={{ padding: '0 8px', fontSize: vratit ? 14 : 16 }}>{vratit ? `Vráceno ${kc(vratit)}` : 'Doručeno'}</Tlacitko>
                    : <Tlacitko plne barva={AKCENT} Icon={ocekavanaPlatba(z) === 'kartou' ? CreditCard : Banknote} onClick={onPlatba} disabled={pracuje} vyska={50} style={{ padding: '0 8px', fontSize: 16 }}>{kc(z.castka_kc)}</Tlacitko>}
            </div>
        </>
    );

    return (
        <>
            {/* Zákazník výrazně, stav sledování (šedé → zelené). Za jízdy vpravo
                malé Volat; na místě je Volat velké v akcích a místo malého
                naskočí Účtenka. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Číslo objednávky velké — podle něj rozvozce hledá tašku (2026-10-22) */}
                <div style={{ fontFamily: 'var(--pismo-display)', fontSize: 32, lineHeight: 1, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                    #{z.cislo_denni ?? z.cislo_objednavky}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {druhyPokus && <Nadpis barva={POZOR}>Druhý pokus</Nadpis>}
                    <div style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {z.jmeno || 'Zákazník'}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        zastávka {poradi} z {celkem}
                    </div>
                </div>
                <IkonaStavu Icon={MessageSquare} zapnuto={smsOdeslana} popis={smsOdeslana ? 'SMS odeslána' : 'SMS odejde sama, až budeš blízko'} />
                <IkonaStavu Icon={Eye} zapnuto={zakaznikVidi} popis={zakaznikVidi ? 'Zákazník tě vidí v mapě' : 'Zákazník mapu zatím neotevřel'} />
                <span style={{ width: 1, alignSelf: 'stretch', margin: '6px 2px', background: 'rgba(255,255,255,0.14)' }} />
                <button type="button" onClick={onUctenka} aria-label="Účtenka" title="Účtenka" style={{ ...ikona, width: 48, color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <Receipt size={19} />
                </button>
            </div>

            {!rozbaleno && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', textAlign: 'center' }}>
                        <Cislo hodnota={hm(z.cas_predpoklad)} popis="u zákazníka" barva={pozde ? POZOR : OK} />
                        <Cislo hodnota={etaSec != null ? String(Math.max(1, Math.round(etaSec / 60))) : '—'} popis="min" />
                        <Cislo hodnota={distanceM == null ? '—' : distanceM >= 1000 ? (distanceM / 1000).toFixed(1).replace('.', ',') : String(distanceM)} popis={distanceM != null && distanceM < 1000 ? 'm' : 'km'} />
                    </div>
                    {onPrepnout && (
                        <button type="button" onClick={onPrepnout} aria-label="Ukázat poznámku a co předat" title="Poznámka a co předat" style={{ ...ikona, width: 44, height: 44, color: 'var(--text-primary)', cursor: 'pointer' }}>
                            <ChevronUp size={22} />
                        </button>
                    )}
                </div>
            )}
            {!rozbaleno && akce}

            {rozbaleno && (
                <>
                    {onPrepnout && !naMiste && (
                        <button type="button" onClick={onPrepnout} aria-label="Schovat poznámku" style={{ alignSelf: 'center', height: 28, padding: '0 14px', borderRadius: 99, border: 'none', background: 'rgba(255,255,255,0.08)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                            <ChevronDown size={16} /> Schovat
                        </button>
                    )}
                    {zaplaceno && <div><Stitek barva={OK} velky>{stitekPlatby(z).text}</Stitek></div>}
                    {z.poznamka && (
                        <div style={{ padding: '10px 12px', borderRadius: 12, background: tint(POZOR, 0.1), border: `1px solid ${tint(POZOR, 0.4)}`, fontSize: 14, fontWeight: 700 }}>
                            „{z.poznamka}“
                        </div>
                    )}
                    {z.soupis && <Predej soupis={z.soupis} />}
                    {druhyPokus ? (
                        <>
                            {/* Nezastižený zákazník — volat je teď to hlavní (2026-10-24). */}
                            <Tlacitko Icon={Phone} href={tel} disabled={!tel} vyska={54} style={{ ...sklenene, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                                Volat zákazníkovi
                            </Tlacitko>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                <Tlacitko barva={OK} onClick={zaplaceno ? onDoruceno : onPlatba} disabled={pracuje} style={sklenene}>Zastihl jsem, předat</Tlacitko>
                                <Tlacitko barva={CHYBA} Icon={Undo2} onClick={onNezastizen} style={sklenene}>Zpět do bistra</Tlacitko>
                            </div>
                        </>
                    ) : akce}
                </>
            )}
        </>
    );
}

/**
 * Kouřové sklo nad mapou. `skloMaterial` (bílá 8 %) je stavěné na tmavé
 * pozadí aplikace; nad světlou mapou z něj bylo mléko a text se ztrácel.
 * Tady tmavý závoj s rozostřením a sytostí — mapa prosvítá, text drží.
 */
const skloMapa = {
    // Sklo je tmavé vždy (i ve světlém režimu), tokeny textu z app.css ale
    // ne — šedé popisky (adresa, jednotky) se na displeji za jízdy ztrácely.
    // Na skle proto vlastní světlé odstíny (2026-10-26).
    '--text-primary': '#F7F7F9',
    '--text-secondary': '#E4E4EA',
    '--text-muted': '#C4C4CE',
    background: 'rgba(16,16,20,0.74)',
    backdropFilter: 'blur(22px) saturate(160%)',
    WebkitBackdropFilter: 'blur(22px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 10px 28px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)',
};

/**
 * „Předej" u dveří: položky se štítkem, taška s přílohami, nápoje — ať zákazník
 * dostane všechno. S čím rozvozce odjel bez (kontrola u regálu), svítí
 * jako upozornění: pokladna to už ví a dořeší.
 */
function Predej({ soupis: s }) {
    const radky = [];
    // Ikona balíčku + počet, ne slovo „krabice" — se štítkem jde i sáček (2026-10-23).
    if (s.krabic > 0) radky.push({ k: 'krabice', Ikona: Package, text: `${s.krabic}×`, pod: s.krabic > 1 ? Array.from({ length: s.krabic }, (_, i) => `${i + 1}/${s.krabic}`).join(' · ') : null });
    // Společné štítky po skupinách (Přílohy…); starší server posílá jen tašku příloh.
    (s.skupiny ?? (s.taska ? [{ nazev: 'Taška s přílohami', obsah: s.taska.obsah }] : []))
        .forEach((g, i) => radky.push({ k: `t${i}`, text: g.nazev, pod: g.obsah.map(t => `${t.pocet}× ${t.nazev}`).join(' · ') }));
    // Bez štítku — přímo položka.
    (s.polozky ?? s.napoje).forEach((n, i) => radky.push({ k: `n${i}`, text: `${n.pocet}× ${n.nazev}` }));
    if (radky.length === 0) return null;
    const chybi = (s.chybi || []).map(c => `${c.pocet}× ${c.nazev}`).join(', ');

    return (
        <div style={{ padding: '10px 12px', borderRadius: 14, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
            <Nadpis barva={AKCENT} style={{ fontSize: 11 }}>Předej</Nadpis>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {radky.map(r => (
                    <div key={r.k} style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                        <CheckCircle2 size={16} style={{ color: OK, flexShrink: 0, alignSelf: 'center' }} />
                        {r.Ikona && <r.Ikona size={17} style={{ flexShrink: 0, alignSelf: 'center' }} />}
                        <span style={{ fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap' }}>{r.text}</span>
                        {r.pod && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.pod}</span>}
                    </div>
                ))}
            </div>
            {chybi && (
                <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 10, background: tint(POZOR, 0.14), color: POZOR, fontSize: 14, fontWeight: 700 }}>
                    Nevezeš {chybi} — řekni zákazníkovi, pokladna už ví.
                </div>
            )}
        </div>
    );
}

/** Vedlejší akce ve skle — plná zůstává jen hlavní (platba / Doručeno). */
const sklenene = { boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' };

const ikona = {
    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none',
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
};

function Cislo({ hodnota, popis, barva }) {
    return (
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 23, fontWeight: 800, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', color: barva ?? 'var(--text-primary)' }}>{hodnota}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)' }}>{popis}</div>
        </div>
    );
}

/** Benzinka / obchod na řadě — kam, jak dlouho, a pak dál. */
function MistoNaTrase({ misto: m, zastavky, aktualniIdx, etaSec, distanceM, busy, onUctenka, onHotovo }) {
    const Ik = m.druh === 'benzinka' ? Fuel : ShoppingCart;
    const dalsi = zastavky.slice(aktualniIdx).filter(z => (z.stav_doruceni ?? 'ceka') === 'ceka').slice(0, 2);
    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tint(AKCENT, 0.18), color: AKCENT }}>
                    <Ik size={24} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Nadpis barva={AKCENT}>{cinnostMista(m.druh)} · počítá se {m.pobyt_min} min</Nadpis>
                    <div style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.nazev}</div>
                    {m.adresa && <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.adresa}</div>}
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', textAlign: 'center' }}>
                <Cislo hodnota={etaSec != null ? String(Math.max(1, Math.round(etaSec / 60))) : '—'} popis="min jízdy" />
                <Cislo hodnota={distanceM == null ? '—' : distanceM >= 1000 ? (distanceM / 1000).toFixed(1).replace('.', ',') : String(distanceM)} popis={distanceM != null && distanceM < 1000 ? 'm' : 'km'} />
            </div>
            {dalsi.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {dalsi.map(z => (
                        <div key={z.objednavka_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontVariantNumeric: 'tabular-nums' }}>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Potom #{z.cislo_denni ?? z.cislo_objednavky} · {ulice(z.adresa)}</span>
                            <span style={{ flexShrink: 0, color: (z.rozdil_sec ?? 0) > 0 ? POZOR : 'var(--text-secondary)' }}>
                                {hm(z.cas_predpoklad)}{z.cas_slibeny ? ` · slib ${String(z.cas_slibeny).slice(0, 5)}` : ''}
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: onUctenka ? 'minmax(0, 1fr) minmax(0, 2fr)' : '1fr', gap: 8 }}>
                {onUctenka && (
                    <Tlacitko Icon={Camera} onClick={onUctenka} vyska={54} style={{ ...sklenene, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', padding: '0 8px' }}>
                        Účtenka
                    </Tlacitko>
                )}
                <Tlacitko plne barva={OK} Icon={busy ? Loader2 : CheckCircle2} onClick={onHotovo} disabled={busy} vyska={54}>
                    Hotovo, jedu dál
                </Tlacitko>
            </div>
        </>
    );
}

function CestaZpet({ zpetDoBistra, busy, onUzavrit }) {
    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800 }}>
                <Home size={18} style={{ color: OK }} /> Všechny zastávky vyřízené · jeď do bistra
            </div>
            <CekaNaPobocce />
            {zpetDoBistra.length > 0 && (
                <div style={{ padding: '10px 12px', borderRadius: 12, background: tint(CHYBA, 0.1), border: `1.5px solid ${tint(CHYBA, 0.5)}`, fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Undo2 size={16} style={{ color: CHYBA, flexShrink: 0 }} />
                    Vrať do bistra: {zpetDoBistra.map(z => `#${z.cislo_denni ?? z.cislo_objednavky}`).join(', ')}
                </div>
            )}
            <Tlacitko plne barva={OK} Icon={busy ? Loader2 : CheckCircle2} onClick={onUzavrit} disabled={busy} vyska={64}>
                Jsem zpět, ukončit rozvoz
            </Tlacitko>
        </>
    );
}

/**
 * Kolik rozvozů čeká na pobočce (2026-10-29) — rozvozce cestou zpět ví,
 * jestli spěchat. Jen bez dávky: hotové v teple, v kuchyni, přijaté.
 */
function CekaNaPobocce() {
    const [obj, setObj] = useState(null);
    useEffect(() => {
        let zivy = true;
        const nacti = () => fetch('/api/rozvoz/prehled', { credentials: 'same-origin' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (zivy && d) setObj(d.objednavky ?? []); })
            .catch(() => {});
        nacti();
        const t = setInterval(nacti, 20000);
        window.addEventListener('app-resumed', nacti);
        return () => { zivy = false; clearInterval(t); window.removeEventListener('app-resumed', nacti); };
    }, []);
    if (!obj) return null;

    const pocet = (stav) => obj.filter(o => o.stav === stav).length;
    const hotove = pocet('hotova');
    const vKuchyni = pocet('v_kuchyni');
    const prijate = pocet('prijata');
    const celkem = hotove + vKuchyni + prijate;
    // Nejbližší slib mezi čekajícími — „HH:MM", řadí se jako text.
    const slib = obj.filter(o => ['hotova', 'v_kuchyni', 'prijata'].includes(o.stav) && o.cas_slibeny)
        .map(o => String(o.cas_slibeny).slice(0, 5)).sort()[0];
    const barva = hotove > 0 ? CHYBA : celkem > 0 ? POZOR : OK;

    return (
        <div style={{ padding: '10px 12px', borderRadius: 12, background: tint(barva, 0.1), border: `1.5px solid ${tint(barva, 0.5)}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Store size={20} style={{ color: barva, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 900 }}>
                    {celkem === 0 ? 'Na pobočce nic nečeká' : `Na pobočce čeká ${celkem} ${celkem === 1 ? 'rozvoz' : celkem < 5 ? 'rozvozy' : 'rozvozů'}`}
                </div>
                {celkem > 0 && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>
                        {[hotove && `${hotove} hotové v teple`, vKuchyni && `${vKuchyni} v kuchyni`, prijate && `${prijate} přijaté`].filter(Boolean).join(' · ')}
                        {slib ? ` · nejbližší slib ${slib}` : ''}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Stav sledování jako ikona: šedá, dokud se nic nestalo, zelená, když ano. */
function IkonaStavu({ Icon, zapnuto, popis }) {
    return (
        <span role="img" aria-label={popis} title={popis} style={{
            ...ikona,
            ...(zapnuto ? { background: tint(OK, 0.14), border: `1.5px solid ${tint(OK, 0.6)}` } : null),
            color: zapnuto ? OK : 'var(--text-muted)',
        }}>
            <Icon size={18} />
        </span>
    );
}
